import React, { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Tabs, 
  Tab, 
  Snackbar,
  Card,
  useTheme,
  alpha,
  Button,
  Chip
} from '@mui/material';
import ProcedureRequestList from '../../components/ProcedureRequestList/ProcedureRequestList';
import ProcedureRequestDetail from '../../components/ProcedureRequestDetail/ProcedureRequestDetail';
import { Refresh as RefreshIcon, Sort as SortIcon, ArrowUpward as ArrowUpwardIcon, ArrowDownward as ArrowDownwardIcon } from '@mui/icons-material';
import { 
  fetchAllProcedureRequests,
  updateProcedureRequest, 
  fetchProcedureRequestsByDoctor
} from '../../services/healinApi';
import { useAuth } from '../../auth/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslations } from '../../translations/translations';

function PlanOfCareHub() {
  const theme = useTheme();
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = useTranslations(language);
  const [availableProcedureRequests, setAvailableProcedureRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [claimedProcedureRequests, setClaimedProcedureRequests] = useState([]);
  const [issuedProcedureRequests, setIssuedProcedureRequests] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selectedProcedureRequest, setSelectedProcedureRequest] = useState(null);
  
  // Sort state
  const [sortBy, setSortBy] = useState('date-desc'); // Default to newest first
  const [sortedAvailableProcedureRequests, setSortedAvailableProcedureRequests] = useState([]);
  const [sortedClaimedProcedureRequests, setSortedClaimedProcedureRequests] = useState([]);
  const [sortedIssuedProcedureRequests, setSortedIssuedProcedureRequests] = useState([]);

  // Sort function
  const sortProcedureRequests = (requestsToSort, sortType) => {
    if (!requestsToSort || requestsToSort.length === 0) return [];
    
    const sorted = [...requestsToSort];
    
    switch (sortType) {
      case 'date-asc':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.requestedAt || a.createdAt || 0);
          const dateB = new Date(b.requestedAt || b.createdAt || 0);
          return dateA - dateB;
        });
      case 'date-desc':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.requestedAt || a.createdAt || 0);
          const dateB = new Date(b.requestedAt || b.createdAt || 0);
          return dateB - dateA;
        });
      case 'status':
        return sorted.sort((a, b) => {
          const statusA = (a.status || '').toLowerCase();
          const statusB = (b.status || '').toLowerCase();
          return statusA.localeCompare(statusB);
        });
      case 'patient':
        return sorted.sort((a, b) => {
          const nameA = (a.patientName || '').toLowerCase();
          const nameB = (b.patientName || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
      case 'none':
      default:
        return sorted;
    }
  };

  // Handle sort change
  const handleSortChange = (newSortType) => {
    setSortBy(newSortType);
    const sortedAvailable = sortProcedureRequests(availableProcedureRequests, newSortType);
    const sortedClaimed = sortProcedureRequests(claimedProcedureRequests, newSortType);
    const sortedIssued = sortProcedureRequests(issuedProcedureRequests, newSortType);
    setSortedAvailableProcedureRequests(sortedAvailable);
    setSortedClaimedProcedureRequests(sortedClaimed);
    setSortedIssuedProcedureRequests(sortedIssued);
  };

  // Update sorted procedure requests when requests change
  useEffect(() => {
    const sortedAvailable = sortProcedureRequests(availableProcedureRequests, sortBy);
    const sortedClaimed = sortProcedureRequests(claimedProcedureRequests, sortBy);
    const sortedIssued = sortProcedureRequests(issuedProcedureRequests, sortBy);
    setSortedAvailableProcedureRequests(sortedAvailable);
    setSortedClaimedProcedureRequests(sortedClaimed);
    setSortedIssuedProcedureRequests(sortedIssued);
  }, [availableProcedureRequests, claimedProcedureRequests, issuedProcedureRequests, sortBy]);

  // Get sort button text and icon
  const getSortButtonText = () => {
    switch (sortBy) {
      case 'date-asc':
        return t.planOfCare.dateOldestFirst;
      case 'date-desc':
        return t.planOfCare.dateNewestFirst;
      case 'status':
        return t.planOfCare.statusAZ;
      case 'patient':
        return t.planOfCare.patientNameAZ;
      case 'none':
      default:
        return t.planOfCare.noSort;
    }
  };

  const getSortIcon = () => {
    switch (sortBy) {
      case 'date-asc':
        return <ArrowUpwardIcon />;
      case 'date-desc':
        return <ArrowDownwardIcon />;
      case 'status':
      case 'patient':
        return <SortIcon />;
      case 'none':
      default:
        return <SortIcon />;
    }
  };

  // Cycle through sort options
  const cycleSortOption = () => {
    const sortOptions = ['date-desc', 'date-asc', 'patient', 'status', 'none'];
    const currentIndex = sortOptions.indexOf(sortBy);
    const nextIndex = (currentIndex + 1) % sortOptions.length;
    handleSortChange(sortOptions[nextIndex]);
  };

  // Fetch available procedure requests using new batch API
  const fetchAvailableProcedureRequests = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('=== FETCHING AVAILABLE PROCEDURE REQUESTS ===');
      
      // Use new batch API to fetch pending procedure requests with Plan Data enrichment
      const procedureRequests = await fetchAllProcedureRequests({
        statuses: ['PENDING'], // Only fetch pending procedure requests
        includePlanData: true,  // Include Plan Data for available requests
        includeUserData: false  // Don't include User Data for available requests
      });
      
      console.log('Fetched procedure requests:', procedureRequests.length);
      
      // Debug: Log the first procedure request to see the data structure
      if (procedureRequests.length > 0) {
        console.log('Sample procedure request data:', procedureRequests[0]);
        console.log('Sample procedures array:', procedureRequests[0].procedures);
        console.log('Sample procedureIds array:', procedureRequests[0].procedureIds);
        console.log('Sample planDetail:', procedureRequests[0].planDetail);
        console.log('Sample planDetail.ScheduledProcedures:', procedureRequests[0].planDetail?.ScheduledProcedures);
      }
      
      // Process procedure requests and map procedure details properly
      const processedProcedureRequests = procedureRequests.map(request => {
        // Map procedures to the format expected by ProcedureRequestCard
        let mappedProcedures = [];
        
        // Check for procedureIds array (new API format)
        if (request.procedureIds && request.procedureIds.length > 0) {
          console.log(`Processing ${request.procedureIds.length} procedure IDs for request ${request.requestId}:`, request.procedureIds);
          
          // Try to get procedure details from planDetail.ScheduledProcedures
          const scheduledProcedures = request.planDetail?.ScheduledProcedures || [];
          console.log(`Available scheduled procedures:`, scheduledProcedures);
          
          mappedProcedures = request.procedureIds.map((procedureId, index) => {
            // Find the matching procedure in ScheduledProcedures
            // Check both ID_ProcedimentoEsp and ID_Procedure fields
            const procedureDetail = scheduledProcedures.find(proc => 
              proc.ID_ProcedimentoEsp === procedureId || proc.ID_Procedure === procedureId
            );
            
            if (procedureDetail) {
              console.log(`Found procedure detail for ${procedureId}:`, procedureDetail);
              return {
                id: procedureId,
                name: procedureDetail.Name_Procedure || `Procedure ${procedureId}`,
                procedureName: procedureDetail.Name_Procedure || `Procedure ${procedureId}`,
                description: `${procedureDetail.Procedure_Class || 'Medical Procedure'}`,
                type: procedureDetail.Procedure_Class || 'Medical Procedure',
                procedureId: procedureId,
                procedureClass: procedureDetail.Procedure_Class,
                numberProcedures: procedureDetail.Number_Procedures,
                completedCount: procedureDetail.CompletedCount || 0,
                completionDates: procedureDetail.CompletionDates || []
              };
            } else {
              console.log(`No procedure detail found for ${procedureId}, using fallback`);
              return {
                id: procedureId,
                name: `Procedure ${procedureId}`,
                procedureName: `Procedure ${procedureId}`,
                description: `Medical procedure with ID: ${procedureId}`,
                type: 'Medical Procedure',
                procedureId: procedureId
              };
            }
          });
          console.log(`Mapped procedures from IDs with enriched data:`, mappedProcedures);
        }
        // Fallback to procedures array (old API format)
        else if (request.procedures && request.procedures.length > 0) {
          console.log(`Processing ${request.procedures.length} procedures for request ${request.requestId}:`, request.procedures);
          mappedProcedures = request.procedures.map((procedure, index) => ({
            id: procedure.procedureId || `proc-${index}`,
            name: procedure.procedureName || procedure.name || 'Unknown Procedure',
            procedureName: procedure.procedureName || procedure.name || 'Unknown Procedure',
            description: procedure.description || 'No description available',
            type: procedure.type || procedure.category || null,
            procedureId: procedure.procedureId
          }));
          console.log(`Mapped procedures:`, mappedProcedures);
        } else {
          console.log(`No procedures or procedureIds found for request ${request.requestId}. Raw request:`, request);
        }

        // Use patient name from userDetail if available (for available requests with Plan Data)
        const patientName = request.userDetail?.FirstName && request.userDetail?.LastName 
          ? `${request.userDetail.FirstName} ${request.userDetail.LastName}`
          : `Patient ${request.userId.slice(-8)}`; // Show last 8 chars of userId as fallback

        return {
          ...request,
          procedures: mappedProcedures, // Override with properly mapped procedures
          patientName: patientName,
          patientInfo: {
            userId: request.userId,
            age: 'N/A',
            gender: request.userDetail?.Sex || 'N/A',
            bloodType: 'N/A',
            allergies: [],
            chronicConditions: [],
            email: request.userDetail?.Email,
            phone: request.userDetail?.Phone_Number,
            address: request.userDetail?.Address,
            city: request.userDetail?.City,
            country: request.userDetail?.Country,
            postalCode: request.userDetail?.PostalCode,
            dateOfBirth: request.userDetail?.DateofBirth,
            fiscalNumber: request.userDetail?.FiscalNumber
          }
        };
      });
      
      console.log('Processed available procedure requests:', processedProcedureRequests.length);
      setAvailableProcedureRequests(processedProcedureRequests);
    } catch (err) {
      console.error('Error fetching available procedure requests:', err);
      setError('Failed to load available procedure requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle claim procedure request
  const handleClaimProcedureRequest = async (requestId) => {
    if (!user || !user.username) {
      setSnackbar({
        open: true,
        message: 'You must be logged in to claim procedure requests',
        severity: 'error'
      });
      return;
    }

    try {
      // Find the procedure request to claim
      const procedureRequestToClaim = availableProcedureRequests.find(req => req.requestId === requestId);
      if (!procedureRequestToClaim) {
        throw new Error('Procedure request not found');
      }

      // Update procedure request status to CLAIMED
      await updateProcedureRequest({
        userId: procedureRequestToClaim.userId,
        requestId: requestId,
        status: 'CLAIMED',
        assignedDoctorId: user.username
      });

      // Remove from available list and add to claimed list
      setAvailableProcedureRequests(availableProcedureRequests.filter(req => req.requestId !== requestId));
      setClaimedProcedureRequests([...claimedProcedureRequests, {
        ...procedureRequestToClaim,
        status: 'CLAIMED',
        assignedDoctorId: user.username,
        assignedAt: new Date().toISOString()
      }]);

      setSnackbar({
        open: true,
        message: t.planOfCare.claimSuccessMessage,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error claiming procedure request:', err);
      setSnackbar({
        open: true,
        message: `Failed to claim procedure request: ${err.message}`,
        severity: 'error'
      });
    }
  };

  // Issue a procedure request
  const handleIssueProcedureRequest = async (procedureRequestData) => {
    if (!user) {
      throw new Error('You must be logged in to issue a procedure request');
    }
    
    try {
      // Update the procedure request status to ISSUED
      await updateProcedureRequest({
        userId: procedureRequestData.userId,
        requestId: procedureRequestData.requestId,
        status: 'ISSUED',
        doctorComments: procedureRequestData.comments || '',
        issuedAt: new Date().toISOString()
      });
      
      // Remove from claimed list
      setClaimedProcedureRequests(claimedProcedureRequests.filter(req => req.requestId !== procedureRequestData.requestId));
      
      // Show success message
      setSnackbar({
        open: true,
        message: t.planOfCare.issueSuccessMessage.replace('{count}', procedureRequestData.procedures?.length || 0),
        severity: 'success'
      });
      
      return { success: true };
    } catch (err) {
      console.error('Error issuing procedure request:', err);
      
      // Show error message
      setSnackbar({
        open: true,
        message: `Failed to issue procedure request: ${err.message}. Please try again.`,
        severity: 'error'
      });
      
      throw err;
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedProcedureRequest(null);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchAvailableProcedureRequests();
    fetchClaimedProcedureRequests();
    fetchIssuedProcedureRequests();
  };

  // Fetch claimed procedure requests using the new doctor-specific API
  const fetchClaimedProcedureRequests = useCallback(async () => {
    if (!user || !user.username) {
      setClaimedProcedureRequests([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('=== FETCHING CLAIMED PROCEDURE REQUESTS FOR DOCTOR ===');
      console.log('Doctor ID:', user.username);
      
      // Use the new doctor-specific API to fetch claimed procedure requests with enrichment
      const result = await fetchProcedureRequestsByDoctor(
        user.username,
        ['CLAIMED'], // Only fetch claimed procedure requests
        true,        // Include Plan Data for claimed requests
        true         // Include User Data for claimed requests
      );
      
      const procedureRequests = result.items || [];
      console.log('Fetched claimed procedure requests:', procedureRequests.length);
      
      // Debug: Log the first claimed procedure request to see the data structure
      if (procedureRequests.length > 0) {
        console.log('Sample CLAIMED procedure request data:', procedureRequests[0]);
        console.log('Sample CLAIMED procedureIds array:', procedureRequests[0].procedureIds);
        console.log('Sample CLAIMED planDetail:', procedureRequests[0].planDetail);
        console.log('Sample CLAIMED planDetail.ScheduledProcedures:', procedureRequests[0].planDetail?.ScheduledProcedures);
      }
      
      // Process procedure requests with proper procedure mapping
      const processedProcedureRequests = procedureRequests.map(request => {
        // Map procedures to the format expected by ProcedureRequestCard
        let mappedProcedures = [];
        
        // Check for procedureIds array (new API format)
        if (request.procedureIds && request.procedureIds.length > 0) {
          // Try to get procedure details from planDetail.ScheduledProcedures
          const scheduledProcedures = request.planDetail?.ScheduledProcedures || [];
          console.log(`Available scheduled procedures for claimed request:`, scheduledProcedures);
          
          mappedProcedures = request.procedureIds.map((procedureId, index) => {
            // Find the matching procedure in ScheduledProcedures
            // Check both ID_ProcedimentoEsp and ID_Procedure fields
            const procedureDetail = scheduledProcedures.find(proc => 
              proc.ID_ProcedimentoEsp === procedureId || proc.ID_Procedure === procedureId
            );
            
            if (procedureDetail) {
              return {
                id: procedureId,
                name: procedureDetail.Name_Procedure || `Procedure ${procedureId}`,
                procedureName: procedureDetail.Name_Procedure || `Procedure ${procedureId}`,
                description: `${procedureDetail.Procedure_Class || 'Medical Procedure'}`,
                type: procedureDetail.Procedure_Class || 'Medical Procedure',
                procedureId: procedureId,
                procedureClass: procedureDetail.Procedure_Class,
                numberProcedures: procedureDetail.Number_Procedures,
                completedCount: procedureDetail.CompletedCount || 0,
                completionDates: procedureDetail.CompletionDates || []
              };
            } else {
              return {
                id: procedureId,
                name: `Procedure ${procedureId}`,
                procedureName: `Procedure ${procedureId}`,
                description: `Medical procedure with ID: ${procedureId}`,
                type: 'Medical Procedure',
                procedureId: procedureId
              };
            }
          });
        }
        // Fallback to procedures array (old API format)
        else if (request.procedures && request.procedures.length > 0) {
          mappedProcedures = request.procedures.map((procedure, index) => ({
            id: procedure.procedureId || `proc-${index}`,
            name: procedure.procedureName || procedure.name || 'Unknown Procedure',
            procedureName: procedure.procedureName || procedure.name || 'Unknown Procedure',
            description: procedure.description || 'No description available',
            type: procedure.type || procedure.category || null,
            procedureId: procedure.procedureId
          }));
        }

        // Use patient name from request or generate from userId, but prioritize userDetail
        const patientName = request.userDetail?.FirstName && request.userDetail?.LastName 
          ? `${request.userDetail.FirstName} ${request.userDetail.LastName}`
          : request.patientName || `Patient ${request.userId?.slice(-8) || 'Unknown'}`;

        return {
          ...request,
          procedures: mappedProcedures, // Override with properly mapped procedures
          patientName: patientName,
          patientInfo: {
            userId: request.userId,
            age: 'N/A',
            gender: request.userDetail?.Sex || 'N/A',
            bloodType: 'N/A',
            allergies: [],
            chronicConditions: [],
            email: request.userDetail?.Email,
            phone: request.userDetail?.Phone_Number,
            address: request.userDetail?.Address,
            city: request.userDetail?.City,
            country: request.userDetail?.Country,
            postalCode: request.userDetail?.PostalCode,
            dateOfBirth: request.userDetail?.DateofBirth,
            fiscalNumber: request.userDetail?.FiscalNumber
          }
        };
      });
      
      console.log('Processed claimed procedure requests:', processedProcedureRequests.length);
      setClaimedProcedureRequests(processedProcedureRequests);
    } catch (err) {
      console.error('Error fetching claimed procedure requests:', err);
      setError('Failed to load claimed procedure requests. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch issued procedure requests using the doctor-specific API
  const fetchIssuedProcedureRequests = useCallback(async () => {
    if (!user || !user.username) {
      setIssuedProcedureRequests([]);
      return;
    }
    
    try {
      console.log('=== FETCHING ISSUED PROCEDURE REQUESTS FOR DOCTOR ===');
      console.log('Doctor ID:', user.username);
      
      // Use the doctor-specific API to fetch issued procedure requests with enrichment
      const result = await fetchProcedureRequestsByDoctor(
        user.username,
        ['ISSUED'], // Only fetch issued procedure requests
        true,       // Include Plan Data for issued requests
        true        // Include User Data for issued requests
      );
      
      const procedureRequests = result.items || [];
      console.log('Fetched issued procedure requests:', procedureRequests.length);
      
      // Process procedure requests similar to claimed procedure requests
      const processedProcedureRequests = procedureRequests.map(request => {
        // Map procedures to the format expected by ProcedureRequestCard
        let mappedProcedures = [];
        
        // Check for procedureIds array (new API format)
        if (request.procedureIds && request.procedureIds.length > 0) {
          // Try to get procedure details from planDetail.ScheduledProcedures
          const scheduledProcedures = request.planDetail?.ScheduledProcedures || [];
          console.log(`Available scheduled procedures for issued request:`, scheduledProcedures);
          
          mappedProcedures = request.procedureIds.map((procedureId, index) => {
            // Find the matching procedure in ScheduledProcedures
            // Check both ID_ProcedimentoEsp and ID_Procedure fields
            const procedureDetail = scheduledProcedures.find(proc => 
              proc.ID_ProcedimentoEsp === procedureId || proc.ID_Procedure === procedureId
            );
            
            if (procedureDetail) {
              return {
                id: procedureId,
                name: procedureDetail.Name_Procedure || `Procedure ${procedureId}`,
                procedureName: procedureDetail.Name_Procedure || `Procedure ${procedureId}`,
                description: `${procedureDetail.Procedure_Class || 'Medical Procedure'}`,
                type: procedureDetail.Procedure_Class || 'Medical Procedure',
                procedureId: procedureId,
                procedureClass: procedureDetail.Procedure_Class,
                numberProcedures: procedureDetail.Number_Procedures,
                completedCount: procedureDetail.CompletedCount || 0,
                completionDates: procedureDetail.CompletionDates || []
              };
            } else {
              return {
                id: procedureId,
                name: `Procedure ${procedureId}`,
                procedureName: `Procedure ${procedureId}`,
                description: `Medical procedure with ID: ${procedureId}`,
                type: 'Medical Procedure',
                procedureId: procedureId
              };
            }
          });
        }
        // Fallback to procedures array (old API format)
        else if (request.procedures && request.procedures.length > 0) {
          mappedProcedures = request.procedures.map((procedure, index) => ({
            id: procedure.procedureId || `proc-${index}`,
            name: procedure.procedureName || procedure.name || 'Unknown Procedure',
            procedureName: procedure.procedureName || procedure.name || 'Unknown Procedure',
            description: procedure.description || 'No description available',
            type: procedure.type || procedure.category || null,
            procedureId: procedure.procedureId
          }));
        }

        // Use patient name from request or generate from userId, but prioritize userDetail
        const patientName = request.userDetail?.FirstName && request.userDetail?.LastName 
          ? `${request.userDetail.FirstName} ${request.userDetail.LastName}`
          : request.patientName || `Patient ${request.userId?.slice(-8) || 'Unknown'}`;

        return {
          ...request,
          procedures: mappedProcedures,
          patientName: patientName,
          patientInfo: {
            userId: request.userId,
            age: 'N/A',
            gender: request.userDetail?.Sex || 'N/A',
            bloodType: 'N/A',
            allergies: [],
            chronicConditions: [],
            email: request.userDetail?.Email,
            phone: request.userDetail?.Phone_Number,
            address: request.userDetail?.Address,
            city: request.userDetail?.City,
            country: request.userDetail?.Country,
            postalCode: request.userDetail?.PostalCode,
            dateOfBirth: request.userDetail?.DateofBirth,
            fiscalNumber: request.userDetail?.FiscalNumber
          },
          issueDate: request.issuedAt || request.updatedAt
        };
      });
      
      console.log('Processed issued procedure requests:', processedProcedureRequests.length);
      setIssuedProcedureRequests(processedProcedureRequests);
    } catch (err) {
      console.error('Error fetching issued procedure requests:', err);
      setSnackbar({ 
        open: true, 
        message: `Error fetching issued procedure requests: ${err.message}`, 
        severity: 'error' 
      });
    }
  }, [user]);

  // Initial data fetch
  useEffect(() => {
    fetchAvailableProcedureRequests();
    fetchClaimedProcedureRequests();
    fetchIssuedProcedureRequests();
    
    // Set up polling to refresh data every 5 minutes
    const intervalId = setInterval(() => {
      fetchAvailableProcedureRequests();
      fetchClaimedProcedureRequests();
      fetchIssuedProcedureRequests();
    }, 5 * 60 * 1000);
    
    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, [user, fetchClaimedProcedureRequests, fetchIssuedProcedureRequests]);

  const handleProcedureRequestSelect = (procedureRequest) => {
    setSelectedProcedureRequest(procedureRequest);
  };

  const handleBackToList = () => {
    setSelectedProcedureRequest(null);
  };

  if (loading && !availableProcedureRequests.length && !claimedProcedureRequests.length && !issuedProcedureRequests.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !availableProcedureRequests.length && !claimedProcedureRequests.length && !issuedProcedureRequests.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, mt: 0 }}>
            {t.planOfCare.title}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {t.planOfCare.subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={cycleSortOption}
            startIcon={getSortIcon()}
            sx={{ 
              borderRadius: 8,
              px: 2,
              py: 1,
              minWidth: 200,
              borderColor: alpha(theme.palette.secondary.main, 0.5),
              '&:hover': {
                borderColor: theme.palette.secondary.main,
                bgcolor: alpha(theme.palette.secondary.main, 0.04)
              }
            }}
          >
            {getSortButtonText()}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            sx={{ 
              borderRadius: 8,
              px: 2,
              py: 1,
              borderColor: alpha(theme.palette.primary.main, 0.5),
              '&:hover': {
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            {t.planOfCare.refresh}
          </Button>
        </Box>
      </Box>
      
      {selectedProcedureRequest ? (
        <Box>
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="body1" 
              sx={{ 
                cursor: 'pointer', 
                color: 'primary.main',
                display: 'inline-flex',
                alignItems: 'center',
                '&:hover': { textDecoration: 'underline' }
              }}
              onClick={handleBackToList}
            >
              {t.planOfCare.backToList}
            </Typography>
          </Box>
          <ProcedureRequestDetail
            procedureRequest={selectedProcedureRequest}
            onClose={handleBackToList}
            onIssueProcedureRequest={handleIssueProcedureRequest}
            onIssued={(updatedRequest) => {
              // Update the request in the appropriate list
              if (selectedProcedureRequest.status === 'CLAIMED') {
                setClaimedProcedureRequests(prev => 
                  prev.filter(req => req.requestId !== updatedRequest.requestId)
                );
                setIssuedProcedureRequests(prev => [...prev, updatedRequest]);
              }
              setSelectedProcedureRequest(null);
            }}
            onClaimed={(updatedRequest) => {
              // Update the request in the appropriate list
              if (selectedProcedureRequest.status === 'PENDING') {
                setAvailableProcedureRequests(prev => 
                  prev.filter(req => req.requestId !== updatedRequest.requestId)
                );
                setClaimedProcedureRequests(prev => [...prev, updatedRequest]);
              }
              setSelectedProcedureRequest(null);
            }}
          />
        </Box>
      ) : (
        <>
          <Card sx={{ mb: 3, overflow: 'visible' }}>
            <Box sx={{ px: 3, pt: 2 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="procedure request tabs"
                sx={{
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  },
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    minHeight: 48,
                    py: 1.5,
                    '&.Mui-selected': {
                      color: theme.palette.primary.main
                    }
                  }
                }}
              >
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {t.planOfCare.availableRequests.replace('{count}', availableProcedureRequests.length)}
                      {sortBy !== 'none' && (
                        <Chip 
                          label={getSortButtonText()} 
                          size="small" 
                          color="primary" 
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {t.planOfCare.myClaimedRequests.replace('{count}', claimedProcedureRequests.length)}
                      {sortBy !== 'none' && (
                        <Chip 
                          label={getSortButtonText()} 
                          size="small" 
                          color="primary" 
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  } 
                />
                <Tab 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {t.planOfCare.myIssuedRequests.replace('{count}', issuedProcedureRequests.length)}
                      {sortBy !== 'none' && (
                        <Chip 
                          label={getSortButtonText()} 
                          size="small" 
                          color="primary" 
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  } 
                />
              </Tabs>
            </Box>
            
            <Box sx={{ p: 3 }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 6, py: 4 }}>
                  <CircularProgress size={40} thickness={4} />
                </Box>
              ) : (
                <>
                  {/* Procedure Type Color Legend */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5), borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                      Procedure Type Legend:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                      <Chip label="Laboratório" size="small" sx={{ 
                        bgcolor: alpha(theme.palette.success.main, 0.1), 
                        color: theme.palette.success.main,
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        fontSize: '0.7rem', height: 20 
                      }} />
                      <Chip label="Exames Complementares" size="small" sx={{ 
                        bgcolor: alpha(theme.palette.info.main, 0.1), 
                        color: theme.palette.info.main,
                        border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                        fontSize: '0.7rem', height: 20 
                      }} />
                      <Chip label="Cirurgia" size="small" sx={{ 
                        bgcolor: alpha(theme.palette.error.main, 0.1), 
                        color: theme.palette.error.main,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        fontSize: '0.7rem', height: 20 
                      }} />
                      <Chip label="Consulta" size="small" sx={{ 
                        bgcolor: alpha(theme.palette.warning.main, 0.1), 
                        color: theme.palette.warning.main,
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        fontSize: '0.7rem', height: 20 
                      }} />
                      <Chip label="Terapia" size="small" sx={{ 
                        bgcolor: alpha(theme.palette.secondary.main, 0.1), 
                        color: theme.palette.secondary.main,
                        border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                        fontSize: '0.7rem', height: 20 
                      }} />
                      <Chip label="Outros" size="small" sx={{ 
                        bgcolor: alpha(theme.palette.primary.main, 0.1), 
                        color: theme.palette.primary.main,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                        fontSize: '0.7rem', height: 20 
                      }} />
                    </Box>
                  </Box>

                  {tabValue === 0 && (
                    <>
                      <ProcedureRequestList 
                        procedureRequests={sortedAvailableProcedureRequests} 
                        onSelectProcedureRequest={handleProcedureRequestSelect}
                        onClaimProcedureRequest={handleClaimProcedureRequest}
                        emptyMessage={t.planOfCare.noAvailableRequests}
                      />
                    </>
                  )}
                  
                  {tabValue === 1 && (
                    <>
                      <ProcedureRequestList 
                        procedureRequests={sortedClaimedProcedureRequests} 
                        onSelectProcedureRequest={handleProcedureRequestSelect}
                        onIssueProcedureRequest={handleIssueProcedureRequest}
                        isClaimed={true}
                        emptyMessage={t.planOfCare.noClaimedRequests}
                      />
                    </>
                  )}
                  
                  {tabValue === 2 && (
                    <>
                      <ProcedureRequestList 
                        procedureRequests={sortedIssuedProcedureRequests} 
                        onSelectProcedureRequest={handleProcedureRequestSelect}
                        isIssued={true}
                        emptyMessage={t.planOfCare.noIssuedRequests}
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          </Card>
          
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert 
              onClose={handleSnackbarClose} 
              severity={snackbar.severity} 
              sx={{ 
                width: '100%',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '& .MuiAlert-icon': {
                  alignItems: 'center'
                }
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
}

export default PlanOfCareHub; 